/**
 * Helpers for the new-draw request (budget-scope-of-work-draws.md §4). The form
 * itself is the drilldown sidebar — DrilldownSidebarAddDraw — which is the only
 * way a draw gets created.
 *
 * Fields come from the backend `DrawDisbursement` model (eastview-os
 * app/models/servicing.py). The doc's §4 says the form collects: requested
 * amount, percentComplete, inspection fields, fees, bank details, notes, and an
 * optional subjectPropertyId. Pre-close loans also get
 * `drawDistributedOnOriginationDate` (funded at closing → part of the initial
 * wire); the server defaults it False there and nulls it on acquisitions, so an
 * unchecked box is simply pruned.
 *
 * DELIBERATELY NOT ASKED (server-owned or set later — see
 * scope_of_work_service.create_draw):
 *   drawNumber / totalDrawn  — always overwritten from prior draws
 *   totalHoldback            — defaulted from the loan
 *   scopeOfWork              — embedded server-side via snapshotScopeOfWork
 *   createdBy / updatedBy    — taken from the auth token
 *   drawDisbursedDate, inspectionReportFile, drawRequestId — post-request data,
 *                              not part of creating the request
 *
 * Nothing is required by the Pydantic model (every field has a default), so the
 * two required fields in the form are ours: without a requested amount and a
 * percent complete the model's advance math (totalEligibleAdvance →
 * grossDrawAmount → netDrawDisbursed) resolves to zero and the draw is
 * meaningless.
 *
 * `percentComplete` is a 0–1 fraction (LOS convention) — RffPercentField shows
 * it as a percentage and stores the fraction. The backend's
 * format_percent_complete() also divides by 100 if a value > 1 arrives.
 */
import set from "lodash/set";
import isNil from "lodash/isNil";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

export const PAYMENT_METHODS = ["Wire", "ACH", "Check"];

// UI-only field values (see UI_FIELD_PREFIX below).
export const INSPECTION_PERFORMED = "performed";
export const INSPECTION_NONE = "none";
export const INSPECTION_OPTIONS = [
  { label: "Inspection performed", value: INSPECTION_PERFORMED },
  { label: "No inspection", value: INSPECTION_NONE },
];

/**
 * `DrawDisbursement.drawKind` — option values verbatim from the model
 * (servicing.py: 'day_1' / 'draw' / 'change_order'). The New draw form only
 * offers the first two; change orders aren't entered here.
 *
 * A Day 1 draw is funded at closing against the locked budget, before any
 * inspection-backed draw — the server 422s a `day_1` once a `draw` exists, so
 * the option is hidden then (budget-scope-of-work-draws.md 2026-09-22).
 */
export const DRAW_KIND_DAY_1 = "day_1";
export const DRAW_KIND_DRAW = "draw";
export const DRAW_KIND_OPTIONS = [
  { label: "Day 1 draw (funded at closing)", value: DRAW_KIND_DAY_1 },
  { label: "Inspection-backed draw", value: DRAW_KIND_DRAW },
];

/** A draw the server counts as inspection-backed (drawKind defaults to 'draw'). */
export const isInspectionBackedDraw = (d) =>
  (d?.drawKind || DRAW_KIND_DRAW) === DRAW_KIND_DRAW;

/**
 * The closing date a Day 1 draw defaults to: the note date once closed, else
 * the scheduled closing, else the expected closing. Raw ISO value for the date
 * field — null when the loan has none (the field then stays blank).
 */
export const getClosingDateForDraw = (loan) =>
  loan?.originationDate ||
  loan?.closingScheduledDate ||
  loan?.expectedClosingDate ||
  null;

// Subject-property select options.
export const getPropertyOptions = (subjectProperties) =>
  (subjectProperties || [])
    .filter((p) => p?._id || p?.oid)
    .map((p) => ({
      label:
        addressLabel(p?.address) || addressLabel(p?.propertyAddress) || "Property",
      value: String(p._id || p.oid),
    }));

/**
 * The bank account already on the loan, if any. `bankDetails` lives on
 * ContactBase, so every borrower carries one — we surface the first borrower
 * that actually has an account number so the user can disburse to it without
 * retyping. Returns null when the loan has none (the form then only offers the
 * manual entry fields — nothing is invented).
 */
export const getBankAccountOnFile = (loan) => {
  const borrowers = loan?.borrowers || [];
  for (const b of borrowers) {
    const bank = b?.bankDetails;
    if (!bank) continue;
    if (!bank.bankAccountNumber && !bank.bankRoutingNumber) continue;
    const ownerName =
      bank.bankAccountOwnerName ||
      [b?.firstName, b?.lastName].filter(Boolean).join(" ") ||
      null;
    return {
      nameOfBank: bank.nameOfBank || null,
      bankAccountNumber: bank.bankAccountNumber || null,
      bankRoutingNumber: bank.bankRoutingNumber || null,
      bankAccountOwnerName: ownerName,
      bankAccountOwnerType: bank.bankAccountOwnerType || null,
    };
  }
  return null;
};

/** Last 4 of an account number, for the "on file" card. */
export const maskAccount = (acct) => {
  if (!acct) return null;
  const s = String(acct);
  return s.length <= 4 ? s : `••${s.slice(-4)}`;
};

/** Two-letter badge for the bank card (e.g. "Wells Fargo" → "WF"). */
export const bankInitials = (nameOfBank) => {
  if (!nameOfBank) return "$";
  return String(nameOfBank)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
};

/**
 * UI-only form fields (which inspection branch is showing, whether to disburse
 * to the account on file) are Final Form fields so the panel has one source of
 * truth, but they are not DrawDisbursement fields — they're named with a leading
 * underscore and stripped here so they never reach the API.
 */
export const UI_FIELD_PREFIX = "_";

const prune = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([key, value]) => {
    if (key.startsWith(UI_FIELD_PREFIX)) return;
    if (isNil(value) || value === "" || value === false) return;
    if (typeof value === "object" && !Array.isArray(value)) {
      const nested = prune(value);
      if (Object.keys(nested).length) out[key] = nested;
      return;
    }
    out[key] = value;
  });
  return out;
};

/**
 * Form values → POST body. Dot-path keys are nested (drawBankDetails.*), empties
 * and `false` flags are dropped so the request only carries what was filled in.
 */
export const buildDrawBody = (values) => {
  const nested = {};
  Object.entries(values || {}).forEach(([key, value]) => set(nested, key, value));
  return prune(nested);
};

/**
 * Net disbursement preview — mirrors the full DrawDisbursement advance chain:
 *   totalEligibleAdvance = max(0, percentComplete × totalHoldback − totalDrawn)
 *   grossDrawAmount      = min(drawRequestedAmount, totalEligibleAdvance)
 *   netDrawDisbursed     = gross − fees − fundsAppliedToPayment
 *
 * The eligibility CAP is applied here deliberately. This is the one place the
 * client duplicates server math, because the number is needed before a POST
 * exists — and a footer that shows an amount the server will then reduce lies at
 * exactly the moment accuracy matters most. Pass the loan's `totalHoldback` and
 * `totalDrawnToDate` from the draws rollup; without them the cap is skipped and
 * gross falls back to the requested amount.
 */
export const drawNetPreview = (values, rollup = {}) => {
  const num = (v) => (isNil(v) || v === "" ? 0 : Number(v) || 0);
  const requested = num(values?.drawRequestedAmount);
  const percentComplete = num(values?.percentComplete);
  const holdback = num(rollup?.totalHoldback);
  const drawn = num(rollup?.totalDrawnToDate);

  const hasBasis = holdback > 0;
  const eligible = hasBasis
    ? Math.max(0, percentComplete * holdback - drawn)
    : null;
  const gross = eligible == null ? requested : Math.min(requested, eligible);
  const capped = eligible != null && requested > eligible + 0.5;

  const fees =
    num(values?.inspectionFee) +
    num(values?.wireFee) +
    num(values?.additionalServicingFees);
  const applied = values?.interestPaymentFromDraw
    ? num(values?.fundsAppliedToPayment)
    : 0;

  return {
    requested,
    percentComplete,
    eligible,
    gross,
    capped,
    fees,
    applied,
    net: gross - fees - applied,
  };
};